/**
 * IssueHistoryDrawer — past issues of one newsletter with per-issue KPI chips,
 * expanding into the per-recipient delivery + engagement table.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  Box,
  Text,
  Heading,
  HStack,
  VStack,
  Badge,
  Icon,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tag,
  Skeleton,
  Button,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FiRefreshCw,
  FiChevronDown,
  FiChevronRight,
  FiEye,
  FiMousePointer,
  FiUsers,
  FiAlertTriangle,
  FiSlash,
  FiInbox,
} from 'react-icons/fi';
import moment from 'moment';
import { fetchNewsletterIssues, fetchIssueSends } from '../../../../api/supabase/newsletters';

const ISSUE_STATUS_META = {
  sending: { label: 'Sending', colorScheme: 'blue' },
  sent: { label: 'Sent', colorScheme: 'green' },
  partial: { label: 'Partial', colorScheme: 'yellow' },
  failed: { label: 'Failed', colorScheme: 'red' },
};

const SEND_STATUS_META = {
  queued: { label: 'Queued', colorScheme: 'blue' },
  sent: { label: 'Sent', colorScheme: 'green' },
  failed: { label: 'Failed', colorScheme: 'red' },
  dnc_blocked: { label: 'DNC skipped', colorScheme: 'orange' },
};

function IssueRow({ issue, isExpanded, onToggle }) {
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const expandedBg = useColorModeValue('gray.50', 'gray.750');

  const [sends, setSends] = useState(null);
  const [sendsLoading, setSendsLoading] = useState(false);

  useEffect(() => {
    if (!isExpanded || sends !== null) return;
    let cancelled = false;
    setSendsLoading(true);
    fetchIssueSends(issue.id)
      .then((rows) => { if (!cancelled) setSends(rows); })
      .catch((err) => {
        console.error('[IssueHistoryDrawer] Sends load failed:', err);
        if (!cancelled) setSends([]);
      })
      .finally(() => { if (!cancelled) setSendsLoading(false); });
    return () => { cancelled = true; };
  }, [isExpanded, sends, issue.id]);

  const statusMeta = ISSUE_STATUS_META[issue.status] || ISSUE_STATUS_META.sending;
  const openPct = issue.sentCount > 0 ? Math.round((issue.uniqueOpens / issue.sentCount) * 100) : 0;
  const clickPct = issue.sentCount > 0 ? Math.round((issue.uniqueClicks / issue.sentCount) * 100) : 0;

  return (
    <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" overflow="hidden">
      <Flex
        align="center"
        justify="space-between"
        px={4}
        py={3}
        cursor="pointer"
        _hover={{ bg: hoverBg }}
        onClick={onToggle}
        gap={3}
      >
        <HStack spacing={3} minW={0}>
          <Icon as={isExpanded ? FiChevronDown : FiChevronRight} boxSize={4} color="gray.400" flexShrink={0} />
          <Box minW={0}>
            <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
              {issue.subject || '(no subject)'}
            </Text>
            <Text fontSize="xs" color={mutedColor}>
              {moment(issue.createdAt).format('MMM D, YYYY h:mm A')} ·{' '}
              {issue.triggeredBy === 'manual' ? 'Sent manually' : 'Scheduled send'}
            </Text>
          </Box>
        </HStack>
        <HStack spacing={2} flexShrink={0}>
          <Badge colorScheme={statusMeta.colorScheme} variant="subtle">{statusMeta.label}</Badge>
        </HStack>
      </Flex>

      <Flex px={4} pb={3} gap={2} flexWrap="wrap">
        <Tag size="sm" colorScheme="blue" variant="subtle">
          <Icon as={FiUsers} boxSize={3} mr={1} /> {issue.sentCount}/{issue.audienceCount} sent
        </Tag>
        <Tag size="sm" colorScheme="green" variant="subtle">
          <Icon as={FiEye} boxSize={3} mr={1} /> {issue.uniqueOpens} opens ({openPct}%)
        </Tag>
        <Tag size="sm" colorScheme="purple" variant="subtle">
          <Icon as={FiMousePointer} boxSize={3} mr={1} /> {issue.uniqueClicks} clicks ({clickPct}%)
        </Tag>
        {issue.dncSkippedCount > 0 && (
          <Tag size="sm" colorScheme="orange" variant="subtle">
            <Icon as={FiSlash} boxSize={3} mr={1} /> {issue.dncSkippedCount} DNC skipped
          </Tag>
        )}
        {issue.failedCount > 0 && (
          <Tag size="sm" colorScheme="red" variant="subtle">
            <Icon as={FiAlertTriangle} boxSize={3} mr={1} /> {issue.failedCount} failed
          </Tag>
        )}
      </Flex>

      {isExpanded && (
        <Box bg={expandedBg} borderTopWidth="1px" borderColor={borderColor} maxH="320px" overflowY="auto">
          {sendsLoading ? (
            <VStack p={4} spacing={2} align="stretch">
              <Skeleton h="18px" /><Skeleton h="18px" /><Skeleton h="18px" />
            </VStack>
          ) : (
            <Table size="sm" variant="simple">
              <Thead position="sticky" top={0} bg={expandedBg} zIndex={1}>
                <Tr>
                  <Th fontSize="10px">Recipient</Th>
                  <Th fontSize="10px">Status</Th>
                  <Th fontSize="10px" isNumeric>Opens</Th>
                  <Th fontSize="10px" isNumeric>Clicks</Th>
                </Tr>
              </Thead>
              <Tbody>
                {(sends || []).map((send) => {
                  const meta = SEND_STATUS_META[send.status] || SEND_STATUS_META.queued;
                  return (
                    <Tr key={send.id}>
                      <Td>
                        <Text fontSize="xs" fontWeight="medium">{send.contactName || '—'}</Text>
                        <Text fontSize="10px" color={mutedColor}>{send.email}</Text>
                      </Td>
                      <Td>
                        <Tooltip label={send.error || ''} isDisabled={!send.error} hasArrow fontSize="xs">
                          <Badge colorScheme={meta.colorScheme} variant="subtle" fontSize="10px">
                            {meta.label}
                          </Badge>
                        </Tooltip>
                      </Td>
                      <Td isNumeric fontSize="xs">
                        {send.openCount > 0 ? (
                          <Tag size="sm" colorScheme="green" variant="subtle">
                            <Icon as={FiEye} boxSize={2.5} mr={1} />{send.openCount}
                          </Tag>
                        ) : '—'}
                      </Td>
                      <Td isNumeric fontSize="xs">
                        {send.clickCount > 0 ? (
                          <Tag size="sm" colorScheme="purple" variant="subtle">
                            <Icon as={FiMousePointer} boxSize={2.5} mr={1} />{send.clickCount}
                          </Tag>
                        ) : '—'}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          )}
        </Box>
      )}
    </Box>
  );
}

export default function IssueHistoryDrawer({ isOpen, onClose, newsletter }) {
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    if (!newsletter?.id) return;
    setLoading(true);
    try {
      const rows = await fetchNewsletterIssues(newsletter.id);
      setIssues(rows);
    } catch (err) {
      console.error('[IssueHistoryDrawer] Load failed:', err);
      setIssues([]);
    } finally {
      setLoading(false);
    }
  }, [newsletter?.id]);

  useEffect(() => {
    if (isOpen) {
      setExpandedId(null);
      load();
    }
  }, [isOpen, load]);

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="xl" placement="right">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">
          <Heading size="md">{newsletter?.name}</Heading>
          <HStack mt={1} spacing={3}>
            <Text fontSize="sm" fontWeight="normal" color={mutedColor}>
              Issue history & engagement
            </Text>
            <Button
              size="xs"
              variant="ghost"
              leftIcon={<FiRefreshCw />}
              onClick={load}
              isLoading={loading}
            >
              Refresh
            </Button>
          </HStack>
        </DrawerHeader>
        <DrawerBody py={4}>
          {loading && issues.length === 0 ? (
            <VStack align="stretch" spacing={3}>
              <Skeleton h="90px" borderRadius="lg" />
              <Skeleton h="90px" borderRadius="lg" />
            </VStack>
          ) : issues.length === 0 ? (
            <Flex direction="column" align="center" py={16} gap={2}>
              <Icon as={FiInbox} boxSize={9} color="gray.300" />
              <Text fontSize="sm" color={mutedColor}>
                No issues yet — this newsletter hasn't been sent.
              </Text>
            </Flex>
          ) : (
            <VStack align="stretch" spacing={3}>
              {issues.map((issue) => (
                <IssueRow
                  key={issue.id}
                  issue={issue}
                  isExpanded={expandedId === issue.id}
                  onToggle={() => setExpandedId(expandedId === issue.id ? null : issue.id)}
                />
              ))}
            </VStack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
